/**
 * m.2.1 Uprava P&L — fullscreen. Pretty path /uprava/pl pored /uprava/zlato.
 * Motor: /api/uprava-pl/* (Python cli). Nije BIS modul 1c.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';

type Line = {
    code: string;
    label: string;
    amount?: number;
    indent?: number;
    source?: string;
    group?: string;
    placeholder?: boolean;
    gap?: boolean;
    as_if_booked?: boolean;
};

type Statement = {
    lines?: Line[];
    source?: { label?: string; note?: string };
};

type DrillRow = { key?: string; amount?: number; prihod?: number; note?: string };
type Drill = { title?: string; line?: string; rows?: DrillRow[] };
type DocRow = { dokument?: string; datum?: string; partner?: string; amount: number; n: number; company_id?: number; gl?: boolean };
type Docs = { rows?: DocRow[]; docs?: number; amount?: number; note?: string | null };
type GlLine = { broj?: string; konto?: string; subjekt?: string; duguje: number; potrazuje: number; datum?: string; mtp?: string | null };

type HqRow = { dept?: string; allocate?: boolean; review?: boolean; label?: string };
type Settings = {
    marketing_key?: string;
    hq_mtp?: Record<string, HqRow>;
    allocate_flags?: Record<string, boolean>;
};

const DEPTS = [
    'uprava', 'uprava_ostalo', 'logistika', 'prijevoz', 'racunovodstvo',
    'financije', 'it', 'pravna', 'marketing', 'prodajni_tim',
];

const fmt = (n: number) => Number(n || 0).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const css = `
.uprava-pl { height: 100%; display: flex; flex-direction: column; background: #fff; color: #111; font-family: Arial, Helvetica, sans-serif; }
.uprava-pl header { display: flex; align-items: center; gap: 24px; padding: 10px 16px; border-bottom: 1px solid #111; flex: 0 0 auto; }
.uprava-pl .brand .title { font-size: 18px; letter-spacing: 0.08em; text-transform: uppercase; }
.uprava-pl .brand .sub { font-size: 12px; color: #444; }
.uprava-pl .banner { flex: 1; font-size: 12px; padding: 6px 10px; border: 1px solid #111; background: #ececec; }
.uprava-pl nav button, .uprava-pl .exit { background: #fff; border: 1px solid #111; padding: 6px 12px; font: inherit; cursor: pointer; }
.uprava-pl nav button.on { background: #111; color: #fff; }
.uprava-pl main { flex: 1; min-height: 0; display: flex; }
.uprava-pl aside { width: 280px; border-right: 1px solid #111; padding: 16px; box-sizing: border-box; overflow: auto; }
.uprava-pl aside label, .uprava-pl .settings label { display: block; font-size: 12px; margin-bottom: 12px; }
.uprava-pl select { width: 100%; margin-top: 4px; padding: 6px; border: 1px solid #111; background: #fff; font: inherit; }
.uprava-pl .sheet { flex: 1; overflow: auto; }
.uprava-pl .sheet.split { display: flex; min-height: 0; }
.uprava-pl .pane { flex: 1; overflow: auto; border-left: 1px solid #111; }
.uprava-pl .pane:first-child { border-left: none; }
.uprava-pl table { width: 100%; border-collapse: collapse; font-size: 13px; }
.uprava-pl th, .uprava-pl td { border-bottom: 1px solid #ddd; padding: 7px 12px; text-align: left; vertical-align: top; }
.uprava-pl th { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #111; font-weight: normal; }
.uprava-pl .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.uprava-pl tr.section td { border-bottom: 1px solid #111; background: #f7f7f7; }
.uprava-pl tr.total td { border-top: 1px solid #111; font-weight: bold; }
.uprava-pl tr.placeholder { color: #666; }
.uprava-pl tr.gap td:first-child::after { content: " (rupa)"; font-style: italic; }
.uprava-pl tr.clickable { cursor: pointer; }
.uprava-pl tr.clickable:hover { background: #f0f0f0; }
.uprava-pl tr.active { background: #ececec; }
.uprava-pl .indent-1 td:first-child { padding-left: 28px; }
.uprava-pl .note { font-size: 12px; color: #333; line-height: 1.4; }
.uprava-pl .settings { padding: 16px; }
.uprava-pl .row { display: flex; gap: 16px; align-items: end; margin-bottom: 16px; }
.uprava-pl #save-settings, .uprava-pl .save { border: 1px solid #111; background: #111; color: #fff; padding: 8px 14px; cursor: pointer; }
.uprava-pl pre { margin: 16px; font-size: 12px; white-space: pre-wrap; }
.uprava-pl h2 { margin: 12px 16px 0; font-size: 14px; font-weight: normal; }
.uprava-pl input[type="text"] { font: inherit; border: 1px solid #111; padding: 4px 6px; width: 140px; }
`;

export function UpravaPlModule({ token, locale, onExit }: { token: string; locale: Locale; onExit: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [tab, setTab] = useState<'pnl' | 'settings' | 'quality'>('pnl');
    const [year, setYear] = useState('2026');
    const [axis, setAxis] = useState('tvrtka');
    const [activeLine, setActiveLine] = useState('opex');
    const [statement, setStatement] = useState<Statement | null>(null);
    const [drill, setDrill] = useState<Drill | null>(null);
    const [settings, setSettings] = useState<Settings | null>(null);
    const [meta, setMeta] = useState<unknown>(null);
    const [err, setErr] = useState('');
    const [saveMsg, setSaveMsg] = useState('');

    const loadStatement = useCallback(() => {
        api<Statement>(`/uprava-pl/statement?year=${encodeURIComponent(year)}`, token)
            .then((d) => { setStatement(d); setErr(''); })
            .catch((e: Error) => setErr(e.message));
    }, [token, year]);

    const loadDrill = useCallback(() => {
        const dim = axis === 'tvrtka' ? 'tvrtka' : 'auto';
        api<Drill>(`/uprava-pl/drill?year=${encodeURIComponent(year)}&line=${encodeURIComponent(activeLine)}&dim=${encodeURIComponent(dim)}`, token)
            .then(setDrill)
            .catch((e: Error) => setDrill({ title: e.message, rows: [] }));
    }, [token, year, axis, activeLine]);

    useEffect(() => {
        api<unknown>('/uprava-pl/meta', token).then(setMeta).catch((e: Error) => setErr(e.message));
        api<Settings>('/uprava-pl/settings', token).then(setSettings).catch((e: Error) => setErr(e.message));
    }, [token]);

    const [drillKey, setDrillKey] = useState('');
    const [docs, setDocs] = useState<Docs | null>(null);
    const [activeDoc, setActiveDoc] = useState('');
    const [glLines, setGlLines] = useState<GlLine[]>([]);

    useEffect(() => { loadStatement(); }, [loadStatement]);
    useEffect(() => { loadDrill(); setDrillKey(''); setDocs(null); setActiveDoc(''); setGlLines([]); }, [loadDrill]);

    const openDocs = (key: string) => {
        setDrillKey(key);
        setActiveDoc('');
        setGlLines([]);
        setDocs({ rows: [], note: t('Ucitavam…', 'Loading…') });
        api<Docs>(`/uprava-pl/documents?year=${encodeURIComponent(year)}&line=${encodeURIComponent(activeLine)}&key=${encodeURIComponent(key)}`, token)
            .then(setDocs)
            .catch((e: Error) => setDocs({ rows: [], note: e.message }));
    };

    const openDoc = (d: DocRow) => {
        if (!d.gl || !d.dokument) return;
        setActiveDoc(d.dokument);
        api<{ rows?: GlLine[] }>(`/uprava-pl/document-lines?year=${encodeURIComponent(year)}&company_id=${d.company_id}&dokument=${encodeURIComponent(d.dokument)}`, token)
            .then((r) => setGlLines(r.rows || []))
            .catch(() => setGlLines([]));
    };

    const save = () => {
        if (!settings) return;
        const next: Settings = JSON.parse(JSON.stringify(settings));
        if (next.hq_mtp?.['907']) {
            next.allocate_flags = next.allocate_flags || {};
            next.allocate_flags.marketing = true;
            next.hq_mtp['907'].allocate = true;
            next.hq_mtp['907'].dept = 'marketing';
        }
        setSaveMsg(t('Spremam…', 'Saving…'));
        mutate<{ ok?: boolean; settings?: Settings }>('/uprava-pl/settings', token, 'PUT', next as unknown as Record<string, unknown>)
            .then((r) => {
                if (r.settings) setSettings(r.settings);
                setSaveMsg(t('Spremljeno.', 'Saved.'));
            })
            .catch((e: Error) => setSaveMsg(e.message));
    };

    const patchHq = (code: string, field: keyof HqRow, value: string | boolean) => {
        setSettings((s) => {
            if (!s) return s;
            const hq = { ...(s.hq_mtp || {}) };
            hq[code] = { ...(hq[code] || {}), [field]: value };
            return { ...s, hq_mtp: hq };
        });
    };

    const yearNote = statement?.source
        ? `${statement.source.label || ''}. ${statement.source.note || ''}`
        : '';

    return (
        <div className="uprava-pl">
            <style>{css}</style>
            <header>
                <button type="button" className="exit" onClick={onExit}>{t('Izlaz', 'Exit')}</button>
                <div className="brand">
                    <div className="title">Uprava</div>
                    <div className="sub">Auro Domus P&amp;L</div>
                </div>
                <div className="banner">
                    STAGING / INTERNO / U RAZRADI. Nije GFI. Brojke se mijenjaju, nije finalan iznos.
                </div>
                <nav>
                    <button type="button" className={tab === 'pnl' ? 'on' : ''} onClick={() => setTab('pnl')}>P&amp;L</button>
                    <button type="button" className={tab === 'settings' ? 'on' : ''} onClick={() => setTab('settings')}>{t('Razdioba', 'Allocation')}</button>
                    <button type="button" className={tab === 'quality' ? 'on' : ''} onClick={() => setTab('quality')}>{t('Pokrice', 'Coverage')}</button>
                </nav>
            </header>
            <main>
                {tab === 'pnl' ? (
                    <>
                        <aside>
                            <label>{t('Godina', 'Year')}
                                <select value={year} onChange={(e) => setYear(e.target.value)}>
                                    <option value="2025">2025 dnevnik</option>
                                    <option value="2026">2026 live</option>
                                </select>
                            </label>
                            <p className="note">{err || yearNote}</p>
                            <label>{t('OPEX slom (stari pogled)', 'OPEX split (legacy)')}
                                <select value={axis} onChange={(e) => setAxis(e.target.value)}>
                                    <option value="tvrtka">{t('Tvrtka', 'Company')}</option>
                                    <option value="vrsta_posla">{t('Vrsta posla', 'Business type')}</option>
                                    <option value="poslovnica">{t('Poslovnica', 'Branch')}</option>
                                    <option value="regija">{t('Regija', 'Region')}</option>
                                    <option value="drzava">{t('Drzava', 'Country')}</option>
                                    <option value="djelatnik">{t('Djelatnik', 'Employee')}</option>
                                </select>
                            </label>
                            <p className="note">{t('Klik na redak iskaza otvara drill. Prihod 2026 je Faros 760300. Nabavna 2026 je I-IV (rupa). OPEX i CAPEX su GK; MTP je obogacivanje.', 'Click a line to drill. 2026 revenue is Faros 760300. 2026 COGS is Jan-Apr only (gap). OPEX/CAPEX are GL; cost center is enrichment.')}</p>
                        </aside>
                        <div className="sheet split">
                            <div className="pane">
                                <table>
                                    <thead><tr><th>{t('Iskaz', 'Statement')}</th><th className="num">EUR</th></tr></thead>
                                    <tbody>
                                        {(statement?.lines || []).map((r) => {
                                            const cls = [
                                                r.indent ? `indent-${r.indent}` : '',
                                                r.source === 'section' ? 'section' : 'clickable',
                                                r.group === 'total' ? 'total' : '',
                                                r.placeholder ? 'placeholder' : '',
                                                r.gap ? 'gap' : '',
                                                r.code === activeLine ? 'active' : '',
                                            ].filter(Boolean).join(' ');
                                            const tag = r.as_if_booked ? ' · as-if' : r.placeholder ? ' · slot' : '';
                                            return (
                                                <tr
                                                    key={r.code}
                                                    className={cls}
                                                    onClick={() => { if (r.source !== 'section') setActiveLine(r.code); }}
                                                >
                                                    <td>{r.label}{tag}</td>
                                                    <td className="num">{r.source === 'section' ? '' : fmt(r.amount || 0)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pane">
                                <h2>{drill?.title || drill?.line || 'Drill'}</h2>
                                <table>
                                    <thead><tr><th>{t('Slom', 'Split')}</th><th className="num">EUR</th></tr></thead>
                                    <tbody>
                                        {(drill?.rows || []).map((r, i) => {
                                            const amt = r.amount != null ? r.amount : r.prihod;
                                            return (
                                                <tr
                                                    key={`${r.key || i}`}
                                                    className={['clickable', r.key && r.key === drillKey ? 'active' : ''].filter(Boolean).join(' ')}
                                                    onClick={() => { if (r.key) openDocs(r.key); }}
                                                >
                                                    <td>{r.key || ''}</td>
                                                    <td className="num">{amt == null ? '' : fmt(amt)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <p className="note">{(drill?.rows && drill.rows[0]?.note) || (drill?.rows?.length ? `${drill.rows.length} redaka` : t('Odaberi redak lijevo.', 'Pick a line on the left.'))}</p>
                            </div>
                            <div className="pane">
                                <h2>{drillKey ? `${t('Dokumenti', 'Documents')}: ${drillKey}` : t('Dokumenti', 'Documents')}</h2>
                                <table>
                                    <thead><tr><th>{t('Dokument', 'Document')}</th><th>{t('Datum', 'Date')}</th><th>{t('Partner', 'Partner')}</th><th className="num">EUR</th></tr></thead>
                                    <tbody>
                                        {(docs?.rows || []).map((d, i) => (
                                            <tr
                                                key={`${d.dokument || i}`}
                                                className={[d.gl ? 'clickable' : '', d.dokument && d.dokument === activeDoc ? 'active' : ''].filter(Boolean).join(' ')}
                                                onClick={() => openDoc(d)}
                                            >
                                                <td>{d.dokument || ''}</td>
                                                <td>{d.datum || ''}</td>
                                                <td>{d.partner || ''}</td>
                                                <td className="num">{fmt(d.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="note">
                                    {docs?.docs != null ? `${docs.docs} ${t('dokumenata', 'documents')}, ${fmt(docs.amount || 0)} EUR${(docs.rows?.length || 0) < docs.docs ? ` (${t('prikazano', 'shown')} ${docs.rows?.length})` : ''}. ` : ''}
                                    {docs?.note || (drillKey ? '' : t('Klik na redak sloma otvara dokumente.', 'Click a split row to open documents.'))}
                                </p>
                                {activeDoc ? (
                                    <>
                                        <h2>{t('Knjizenja', 'Postings')}: {activeDoc}</h2>
                                        <table>
                                            <thead><tr><th>{t('Konto', 'Account')}</th><th>MTP</th><th>{t('Partner', 'Partner')}</th><th className="num">{t('Duguje', 'Debit')}</th><th className="num">{t('Potrazuje', 'Credit')}</th></tr></thead>
                                            <tbody>
                                                {glLines.map((l, i) => (
                                                    <tr key={`${l.broj || ''}-${l.konto || ''}-${i}`}>
                                                        <td>{l.konto || ''}</td>
                                                        <td>{l.mtp || ''}</td>
                                                        <td>{l.subjekt || ''}</td>
                                                        <td className="num">{l.duguje ? fmt(l.duguje) : ''}</td>
                                                        <td className="num">{l.potrazuje ? fmt(l.potrazuje) : ''}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </>
                ) : null}
                {tab === 'settings' ? (
                    <div className="sheet settings">
                        <div className="row">
                            <label>{t('Kljuc razdiobe marketinga', 'Marketing allocation key')}
                                <select
                                    value={settings?.marketing_key || 'prihod'}
                                    onChange={(e) => setSettings((s) => (s ? { ...s, marketing_key: e.target.value } : s))}
                                >
                                    <option value="prihod">{t('Udio prihoda poslovnice', 'Branch revenue share')}</option>
                                    <option value="jednako">{t('Jednako po poslovnici', 'Equal per branch')}</option>
                                    <option value="headcount">Headcount</option>
                                    <option value="ruc">RUC</option>
                                    <option value="rucni">{t('Rucni postotci', 'Manual percents')}</option>
                                </select>
                            </label>
                            <button type="button" className="save" onClick={save}>{t('Spremi postavke', 'Save settings')}</button>
                            <span className="note">{saveMsg}</span>
                        </div>
                        <p className="note">{t('Marketing (MTP 907) ide na sve poslovnice. Ostali HQ odjeli ostaju na Upravi dok im ne upalis razdiobu.', 'Marketing (MTP 907) goes to all branches. Other HQ departments stay on Board until allocation is on.')}</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>MTP</th>
                                    <th>{t('Odjel', 'Dept')}</th>
                                    <th>{t('Razdijeli', 'Allocate')}</th>
                                    <th>Review</th>
                                    <th>{t('Biljeska', 'Note')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(settings?.hq_mtp || {}).sort().map((code) => {
                                    const row = settings?.hq_mtp?.[code] || {};
                                    return (
                                        <tr key={code}>
                                            <td>{code}</td>
                                            <td>
                                                <select value={row.dept || 'uprava'} onChange={(e) => patchHq(code, 'dept', e.target.value)}>
                                                    {DEPTS.map((d) => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                            </td>
                                            <td><input type="checkbox" checked={!!row.allocate} onChange={(e) => patchHq(code, 'allocate', e.target.checked)} /></td>
                                            <td><input type="checkbox" checked={!!row.review} onChange={(e) => patchHq(code, 'review', e.target.checked)} /></td>
                                            <td>
                                                <input
                                                    type="text"
                                                    value={row.label || ''}
                                                    onChange={(e) => patchHq(code, 'label', e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
                {tab === 'quality' ? (
                    <div className="sheet">
                        <pre>{JSON.stringify(meta, null, 2)}</pre>
                    </div>
                ) : null}
            </main>
        </div>
    );
}
